const MAX_ROWS = 200;
const MAX_COLS = 32;

export const clampTableDim = (value, min, max) => {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
};

export const emptyTableData = (rows = 3, cols = 3) => ({
  rows: Array.from({ length: clampTableDim(rows, 1, MAX_ROWS) }, () => (
    Array.from({ length: clampTableDim(cols, 1, MAX_COLS) }, () => '')
  )),
});

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export const CHECKBOX_CHECKED = '☑';
export const CHECKBOX_UNCHECKED = '☐';

const hasCheckboxPrefix = (text) => /^\s*(☑|☐|✓|✔|\[\s*\]|\[x\]|\[X\])/i.test(String(text || ''));

export const stripCheckboxPrefix = (text) => String(text || '')
  .replace(/\u00a0/g, ' ')
  .replace(/^\s*(☑|☐|✓|✔|\[\s*\]|\[x\]|\[X\])\s*/i, '')
  .replace(/^\s+|\s+$/g, '');

export const withCheckboxPrefix = (listValue, text) => {
  const raw = String(text || '').replace(/\u00a0/g, ' ').replace(/^\s+|\s+$/g, '');
  if (hasCheckboxPrefix(raw)) return raw;
  if (listValue === 'checked') return raw ? `${CHECKBOX_CHECKED} ${raw}` : CHECKBOX_CHECKED;
  if (listValue === 'unchecked') return raw ? `${CHECKBOX_UNCHECKED} ${raw}` : CHECKBOX_UNCHECKED;
  return raw;
};

export const toggleCheckboxPrefix = (text) => {
  const raw = String(text || '').replace(/\u00a0/g, ' ');
  if (hasCheckboxPrefix(raw)) return stripCheckboxPrefix(raw);
  return withCheckboxPrefix('unchecked', raw);
};

export const renderCellHtml = (value) => {
  const text = String(value ?? '');
  if (!text) return '';
  return text.split('\n').map((line) => {
    const match = String(line).match(/^\s*(☑|☐|✓|✔|\[x\]|\[X\]|\[\s*\])\s*(.*)$/i);
    if (!match) return escapeHtml(line);
    const checked = /☑|✓|✔|\[x\]/i.test(match[1]);
    return (
      `<label class="note-cell-check${checked ? ' is-checked' : ''}">`
      + `<input type="checkbox"${checked ? ' checked' : ''} contenteditable="false">`
      + `<span>${escapeHtml(match[2] || '')}</span>`
      + '</label>'
    );
  }).join('<br>');
};

export const readCellFromDom = (cell) => {
  if (!cell) return '';
  const labels = [...cell.querySelectorAll('label.note-cell-check')];
  if (labels.length) {
    return labels.map((label) => {
      const input = label.querySelector('input[type="checkbox"]');
      const text = String(label.querySelector('span')?.textContent || '')
        .replace(/\u00a0/g, ' ')
        .trim();
      const mark = input?.checked ? CHECKBOX_CHECKED : CHECKBOX_UNCHECKED;
      return text ? `${mark} ${text}` : mark;
    }).join('\n');
  }
  const inputs = [...cell.querySelectorAll('input[type="checkbox"]')];
  if (inputs.length) {
    return inputs.map((input) => {
      const parent = input.closest('label') || input.parentElement;
      const text = String(parent?.textContent || '')
        .replace(/\u00a0/g, ' ')
        .trim();
      const mark = input.checked ? CHECKBOX_CHECKED : CHECKBOX_UNCHECKED;
      return text ? `${mark} ${text}` : mark;
    }).join('\n');
  }
  return String(cell.textContent || '').replace(/\u00a0/g, ' ').trim();
};

export const htmlSelectionToLines = (html) => {
  if (!html || typeof html !== 'string') return [];
  const root = document.createElement('div');
  root.innerHTML = html;
  const blocks = [...root.querySelectorAll('li, p, h1, h2, h3, h4')];
  if (!blocks.length) {
    return String(root.textContent || '')
      .replace(/\r\n/g, '\n')
      .split('\n')
      .map((line) => line.replace(/\s+$/g, ''));
  }
  return blocks
    .filter((el) => el.tagName === 'LI' || !el.closest('li'))
    .map((el) => withCheckboxPrefix(el.getAttribute('data-list'), el.textContent))
    .filter((line, index, all) => line || (index > 0 && index < all.length - 1));
};

const splitCsvLine = (line) => {
  const cells = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      cells.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
};

const splitRow = (line) => {
  const raw = String(line || '').replace(/\u00a0/g, ' ');
  if (raw.includes('\t')) return raw.split('\t').map((cell) => cell.trim());
  if (raw.includes('|')) {
    return raw.split('|').map((cell) => cell.trim()).filter((cell, index, all) => {
      if (all.length <= 2) return cell.length > 0;
      return !(index === 0 && cell === '') && !(index === all.length - 1 && cell === '');
    });
  }
  if (/,/.test(raw) && raw.split(',').length >= 2) return splitCsvLine(raw);
  if (/\s{2,}/.test(raw)) return raw.split(/\s{2,}/).map((cell) => cell.trim()).filter(Boolean);
  return [raw.trim()];
};

const cellTextFromNode = (cell) => {
  if (!cell) return '';
  const holder = document.createElement('div');
  holder.innerHTML = String(cell.innerHTML || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|h[1-6])>/gi, '\n');
  return String(holder.textContent || '').replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').trim();
};

const normalizeTableRows = (rawRows) => {
  const parsed = (rawRows || [])
    .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : []))
    .filter((row) => row.length);
  if (!parsed.length) return null;
  const colCount = Math.min(
    MAX_COLS,
    Math.max(1, parsed.reduce((max, row) => Math.max(max, row.length), 1)),
  );
  const rows = parsed.slice(0, MAX_ROWS).map((row) => {
    const next = row.slice(0, colCount);
    while (next.length < colCount) next.push('');
    return next;
  });
  if (rows.length === 1 && colCount === 1) return null;
  return { rows };
};

export const htmlTableToTableData = (htmlOrNode) => {
  let root = htmlOrNode;
  if (typeof htmlOrNode === 'string') {
    const trimmed = htmlOrNode.trim();
    if (!trimmed || !/<table[\s>]/i.test(trimmed)) return null;
    root = new DOMParser().parseFromString(trimmed, 'text/html')?.body;
  }
  const table = root?.matches?.('table') ? root : root?.querySelector?.('table');
  if (!table) return null;
  const rowEls = [
    ...table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr, :scope > tr'),
  ];
  const rows = rowEls.map((tr) => {
    const cells = [];
    [...tr.children].forEach((cell) => {
      if (!/^(TD|TH)$/i.test(cell.tagName)) return;
      const span = clampTableDim(cell.getAttribute('colspan') || 1, 1, MAX_COLS);
      const text = cellTextFromNode(cell);
      for (let i = 0; i < span && cells.length < MAX_COLS; i += 1) {
        cells.push(i === 0 ? text : '');
      }
    });
    return cells;
  });
  return normalizeTableRows(rows);
};

export const looksLikeSpreadsheetText = (text) => {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.replace(/\s+$/g, ''))
    .filter((line, index, all) => line.trim() || (index > 0 && index < all.length - 1));
  if (!lines.length) return false;
  const tabbed = lines.filter((line) => line.includes('\t'));
  if (tabbed.length >= 1 && tabbed.some((line) => line.split('\t').length >= 2)) return true;
  return false;
};

export const htmlHasNonTableContent = (html) => {
  const raw = String(html || '');
  if (!raw.trim()) return false;
  let body;
  try {
    body = new DOMParser().parseFromString(raw, 'text/html')?.body;
  } catch {
    return /<[a-z][\s\S]*?>/i.test(raw);
  }
  if (!body) return false;
  const clone = body.cloneNode(true);
  clone.querySelectorAll('table, style, script, meta, link, xml').forEach((node) => node.remove());
  return String(clone.textContent || '').replace(/\s+/g, ' ').trim().length > 0;
};

export const tableDataFromClipboard = (clipboardData) => {
  if (!clipboardData) return null;
  const html = clipboardData.getData?.('text/html') || '';
  const fromHtml = htmlTableToTableData(html);
  if (fromHtml) return fromHtml;
  const text = clipboardData.getData?.('text/plain') || '';
  if (!looksLikeSpreadsheetText(text)) return null;
  return normalizeTableRows(textToTableData(text).rows);
};

export const splitPlainIntoTextAndTable = (text) => {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');
  const firstTable = lines.findIndex((line) => line.includes('\t') && line.split('\t').length >= 2);
  if (firstTable < 0) {
    return { prefix: String(text || '').trim(), table: null };
  }
  const prefix = lines.slice(0, firstTable).join('\n').trim();
  const table = normalizeTableRows(textToTableData(lines.slice(firstTable).join('\n')).rows);
  return { prefix, table };
};

export const textToTableData = (text) => {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.replace(/\s+$/g, ''))
    .filter((line, index, all) => line.trim() || (index > 0 && index < all.length - 1));
  const source = lines.length ? lines : [''];
  const parsed = source.map(splitRow);
  const colCount = Math.min(
    MAX_COLS,
    Math.max(1, parsed.reduce((max, row) => Math.max(max, row.length), 1)),
  );
  const rows = parsed.slice(0, MAX_ROWS).map((row) => {
    const next = row.slice(0, colCount);
    while (next.length < colCount) next.push('');
    return next;
  });
  if (!rows.length) return emptyTableData(2, 2);
  return { rows };
};

export const readTableFromDom = (node) => {
  if (!node) return emptyTableData(1, 1);
  const rows = [...node.querySelectorAll('tr')].map((tr) => (
    [...tr.querySelectorAll('th,td')].map((cell) => readCellFromDom(cell))
  )).filter((row) => row.length);
  const meta = tableMetaFromNode(node);
  if (rows.length) return { rows, ...meta };
  try {
    const stored = node.getAttribute('data-table');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed?.rows) && parsed.rows.length) {
        return {
          rows: parsed.rows.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : [''])),
          ...tableMetaFromParsed(parsed),
        };
      }
    }
  } catch {
    // ignored
  }
  return emptyTableData(1, 1);
};

const tableMetaFromParsed = (parsed) => {
  const meta = {};
  if (parsed?.summaryColumns && typeof parsed.summaryColumns === 'object') {
    meta.summaryColumns = parsed.summaryColumns;
  }
  if (Array.isArray(parsed?.colWidths)) meta.colWidths = parsed.colWidths;
  if (typeof parsed?.hasHeader === 'boolean') meta.hasHeader = parsed.hasHeader;
  return meta;
};

const tableMetaFromNode = (node) => {
  try {
    return tableMetaFromParsed(JSON.parse(node.getAttribute('data-table') || '{}'));
  } catch {
    return {};
  }
};

export const MIN_COL_WIDTH = 64;
export const MAX_COL_WIDTH = 640;
export const DEFAULT_COL_WIDTH = 140;

export const getTableColCount = (data) => {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  return Math.max(1, rows.reduce((max, row) => Math.max(max, (row || []).length), 1));
};

export const getColWidths = (data) => {
  const count = getTableColCount(data);
  const stored = Array.isArray(data?.colWidths) ? data.colWidths : [];
  return Array.from({ length: count }, (_, index) => {
    const n = Number(stored[index]);
    if (!Number.isFinite(n)) return DEFAULT_COL_WIDTH;
    return Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.round(n)));
  });
};

export const setTableColWidth = (data, colIndex, widthPx) => {
  const widths = getColWidths(data);
  const index = clampTableDim(colIndex, 0, widths.length - 1);
  const width = Number(widthPx);
  widths[index] = Number.isFinite(width)
    ? Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.round(width)))
    : DEFAULT_COL_WIDTH;
  return { ...data, colWidths: widths };
};

export const addTableRow = (data, afterIndex = null) => {
  const source = Array.isArray(data?.rows) && data.rows.length ? data.rows : emptyTableData(1, 1).rows;
  const colCount = Math.max(1, source.reduce((max, row) => Math.max(max, (row || []).length), 1));
  if (source.length >= MAX_ROWS) return { ...data, rows: source.map((row) => [...(row || [])]) };
  const rows = source.map((row) => {
    const next = [...(row || [])];
    while (next.length < colCount) next.push('');
    return next;
  });
  const insertAt = afterIndex == null || !Number.isFinite(afterIndex)
    ? rows.length
    : Math.max(0, Math.min(rows.length, afterIndex + 1));
  rows.splice(insertAt, 0, Array.from({ length: colCount }, () => ''));
  return { ...data, rows };
};

export const addTableColumn = (data, afterIndex = null) => {
  const source = Array.isArray(data?.rows) && data.rows.length ? data.rows : emptyTableData(1, 1).rows;
  const colCount = Math.max(1, source.reduce((max, row) => Math.max(max, (row || []).length), 1));
  if (colCount >= MAX_COLS) return { ...data, rows: source.map((row) => [...(row || [])]) };
  const insertAt = afterIndex == null || !Number.isFinite(afterIndex)
    ? colCount
    : Math.max(0, Math.min(colCount, afterIndex + 1));
  const rows = source.map((row) => {
    const next = [...(row || [])];
    while (next.length < colCount) next.push('');
    next.splice(insertAt, 0, '');
    return next;
  });
  const summaryColumns = shiftSummaryColumns(data?.summaryColumns, insertAt, 1);
  const colWidths = getColWidths(data);
  colWidths.splice(insertAt, 0, DEFAULT_COL_WIDTH);
  return { ...data, rows, summaryColumns, colWidths };
};

export const deleteTableRow = (data, rowIndex) => {
  const source = Array.isArray(data?.rows) ? data.rows.map((row) => [...(row || [])]) : [];
  if (source.length <= 1) return { ...data, rows: source };
  const index = clampTableDim(rowIndex, 0, source.length - 1);
  source.splice(index, 1);
  const hasHeader = data?.hasHeader && index === 0 ? false : data?.hasHeader;
  return { ...data, rows: source, hasHeader };
};

export const deleteTableColumn = (data, colIndex) => {
  const source = Array.isArray(data?.rows) ? data.rows.map((row) => [...(row || [])]) : [];
  const colCount = Math.max(1, source.reduce((max, row) => Math.max(max, row.length), 1));
  if (colCount <= 1) return { ...data, rows: source };
  const index = clampTableDim(colIndex, 0, colCount - 1);
  const rows = source.map((row) => {
    const next = [...row];
    if (next.length > index) next.splice(index, 1);
    return next.length ? next : [''];
  });
  const summaryColumns = {};
  Object.entries(data?.summaryColumns || {}).forEach(([key, metric]) => {
    const i = Number(key);
    if (!Number.isFinite(i) || i === index) return;
    summaryColumns[i > index ? i - 1 : i] = metric;
  });
  const colWidths = getColWidths(data);
  if (colWidths.length > index) colWidths.splice(index, 1);
  return { ...data, rows, summaryColumns, colWidths };
};

export const addTableHeader = (data) => {
  const source = Array.isArray(data?.rows) && data.rows.length
    ? data.rows.map((row) => [...(row || [])])
    : emptyTableData(1, 1).rows;
  const colCount = Math.max(1, source.reduce((max, row) => Math.max(max, row.length), 1));
  if (data?.hasHeader) {
    const next = addTableColumn(data, colCount);
    if (next.rows[0]) next.rows[0][next.rows[0].length - 1] = `Header ${next.rows[0].length}`;
    return { ...next, hasHeader: true };
  }
  const header = Array.from({ length: colCount }, (_, index) => `Header ${index + 1}`);
  return { ...data, hasHeader: true, rows: [header, ...source] };
};

const shiftSummaryColumns = (summaryColumns, insertAt, delta) => {
  const next = {};
  Object.entries(summaryColumns || {}).forEach(([key, metric]) => {
    const i = Number(key);
    if (!Number.isFinite(i)) return;
    next[i >= insertAt ? i + delta : i] = metric;
  });
  return next;
};

export const applyTableDataToNode = (node, data) => {
  if (!node) return data;
  const next = data && Array.isArray(data.rows) ? data : emptyTableData(1, 1);
  node.setAttribute('contenteditable', 'false');
  node.setAttribute('data-table', JSON.stringify(next));
  node.innerHTML = renderTableHtml(next);
  return next;
};

export const renderTableHtml = (data) => {
  const rows = Array.isArray(data?.rows) && data.rows.length ? data.rows : emptyTableData(2, 2).rows;
  const hasHeader = data?.hasHeader === true;
  const widths = getColWidths(data);
  const tableWidth = widths.reduce((sum, width) => sum + width, 0);
  const renderRow = (row, rowIndex, tagName) => {
    const cells = (row || []).map((cell, colIndex) => {
      const resizer = rowIndex === 0
        ? `<span class="note-col-resizer" data-c="${colIndex}" contenteditable="false"></span>`
        : '';
      const width = widths[colIndex] || DEFAULT_COL_WIDTH;
      return `<${tagName} data-r="${rowIndex}" data-c="${colIndex}" contenteditable="true" style="width:${width}px">${renderCellHtml(cell)}${resizer}</${tagName}>`;
    }).join('');
    return `<tr>${cells}</tr>`;
  };
  const head = hasHeader ? `<thead>${renderRow(rows[0] || [], 0, 'th')}</thead>` : '';
  const bodyRows = hasHeader ? rows.slice(1) : rows;
  const body = bodyRows.map((row, index) => (
    renderRow(row, hasHeader ? index + 1 : index, 'td')
  )).join('');
  const cols = widths.map((width, index) => `<col data-c="${index}" style="width:${width}px">`).join('');
  return (
    `<div class="note-table-actions" contenteditable="false">`
    + '<button type="button" class="note-table-add-header" data-table-action="add-header" title="Add header" aria-label="Add header">+ Header</button>'
    + '<button type="button" class="note-table-add-row" data-table-action="add-row" title="Add row" aria-label="Add row">+ Row</button>'
    + '<button type="button" class="note-table-add-col" data-table-action="add-col" title="Add column" aria-label="Add column">+ Column</button>'
    + '<button type="button" class="note-table-del-row" data-table-action="del-row" title="Remove row" aria-label="Remove row">- Row</button>'
    + '<button type="button" class="note-table-del-col" data-table-action="del-col" title="Remove column" aria-label="Remove column">- Column</button>'
    + '<button type="button" class="note-table-summary-btn" data-table-action="summary" title="Table summary" aria-label="Table summary">'
    + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3h18v18H3z"/><path d="M3 9h18M9 21V9"/><path d="M8 13h2M8 17h5"/></svg>'
    + '</button>'
    + '</div>'
    + `<table class="note-table" style="width:${tableWidth}px;table-layout:fixed"><colgroup>${cols}</colgroup>${head}<tbody>${body}</tbody></table>`
  );
};

export const parseNumericCell = (value) => {
  const raw = String(value ?? '')
    .replace(/^[☑☐✓✔]\s*/, '')
    .replace(/^\[[ xX]?\]\s*/i, '')
    .trim();
  if (!raw) return null;
  const cleaned = raw
    .replace(/[%]/g, '')
    .replace(/[$€£₹]/g, '')
    .replace(/,/g, '')
    .trim();
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const formatNumber = (value) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const SUMMARY_METRICS = [
  { id: 'sum', label: 'Sum' },
  { id: 'avg', label: 'Avg' },
  { id: 'min', label: 'Min' },
  { id: 'max', label: 'Max' },
  { id: 'count', label: 'Count' },
];

export const columnMetricValue = (col, metric) => {
  if (!col) return null;
  if (metric === 'count') return col.numericCount;
  if (metric === 'sum') return col.numericCount ? col.sum : null;
  if (metric === 'avg') return col.avg;
  if (metric === 'min') return col.min;
  if (metric === 'max') return col.max;
  return null;
};

export const defaultSummaryColumns = (summary) => {
  const next = {};
  (summary?.columns || []).forEach((col) => {
    if (col.numericCount > 0) next[String(col.index)] = 'sum';
  });
  return next;
};

export const summarizeTable = (data) => {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const rowCount = rows.length;
  const colCount = rows.reduce((max, row) => Math.max(max, (row || []).length), 0);
  const firstRowNums = (rows[0] || []).filter((cell) => parseNumericCell(cell) != null).length;
  const laterNums = rows.slice(1).reduce((count, row) => (
    count + (row || []).filter((cell) => parseNumericCell(cell) != null).length
  ), 0);
  const hasHeader = data?.hasHeader === true
    || (data?.hasHeader !== false && rowCount > 1 && firstRowNums === 0 && laterNums > 0);
  const dataStart = hasHeader ? 1 : 0;
  let emptyCount = 0;
  let numericCount = 0;
  let grandSum = 0;
  const columns = [];

  for (let col = 0; col < colCount; col += 1) {
    const header = hasHeader
      ? String(rows[0]?.[col] || `Column ${col + 1}`)
      : `Column ${col + 1}`;
    const nums = [];
    for (let row = dataStart; row < rowCount; row += 1) {
      const cell = rows[row]?.[col];
      if (!String(cell || '').trim()) emptyCount += 1;
      const n = parseNumericCell(cell);
      if (n != null) {
        nums.push(n);
        numericCount += 1;
        grandSum += n;
      }
    }
    const sum = nums.reduce((a, b) => a + b, 0);
    columns.push({
      index: col,
      header,
      numericCount: nums.length,
      sum,
      avg: nums.length ? sum / nums.length : null,
      min: nums.length ? Math.min(...nums) : null,
      max: nums.length ? Math.max(...nums) : null,
    });
  }

  return {
    rowCount,
    colCount,
    dataRowCount: Math.max(0, rowCount - dataStart),
    cellCount: rowCount * colCount,
    emptyCount,
    numericCount,
    hasHeader,
    grandSum,
    columns,
  };
};

export { MAX_ROWS, MAX_COLS, escapeHtml, formatNumber };
